import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  CircularProgress,
  Icon,
  Typography,
} from "@mui/material";
import { useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { DndProvider } from "react-dnd";
import { darkenHexColor } from "@utils/Utilities";

// Función para obtener el estilo de las filas
const getStripedStyle = (
  row,
  index,
  backgroundColor = null,
  disableStriped = false
) =>
  row.style ?? disableStriped
    ? backgroundColor
      ? { background: backgroundColor }
      : {}
    : {
        background: backgroundColor
          ? index % 2
            ? darkenHexColor(backgroundColor, 5)
            : backgroundColor
          : index % 2
          ? "#E4ECFA"
          : "white",
      };

const TableDragDrop = ({
  columns,
  data,
  onRowChange,
  stickyHeader = false,
  minWidth = 100,
  minHeight = 50,
  maxHeight = 500,
  backgroundColor = null,
  disableStriped = false,
  loading = false,
}) => {
  // Drag-and-drop item component
  
  const DraggableRow = ({ item, index, moveRow }) => {
    const [, ref] = useDrag({
      type: "ROW",
      item: { index },
    });

    const [, drop] = useDrop({
      accept: "ROW",
      hover: (draggedItem) => {
        if (draggedItem.index !== index) {
          moveRow(draggedItem.index, index);
          draggedItem.index = index;
        }
      },
    });

    return (
      <TableRow
        ref={(node) => ref(drop(node))}
        sx={getStripedStyle(item, index, backgroundColor, disableStriped)} // Aplicamos el estilo a la fila
      >
        {columns.map((column) => (
          <TableCell key={column.id}>{item[column.id]}</TableCell>
        ))}
      </TableRow>
    );
  };

  const moveRow = (fromIndex, toIndex) => {
    const updatedData = [...data];
    const [movedRow] = updatedData.splice(fromIndex, 1);
    updatedData.splice(toIndex, 0, movedRow);

    // Actualizar todas las filas afectadas (tanto las que se movieron como las que quedaron)
    const dataToUpdate = updatedData.map((item, index) => ({
      ...item,
      newRow: index + 1, // Asignar el nuevo índice a cada fila comenzando desde 1
    }));

    onRowChange(updatedData, dataToUpdate); // Pasa los datos actualizados y los cambios
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <TableContainer
        component={Paper}
        sx={{
          maxHeight: maxHeight,
          overflowY: "auto",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        <Table
          aria-label="tabla basica"
          stickyHeader={stickyHeader}
          size={"small"}
          sx={!stickyHeader ? { minWidth: minWidth, minHeight: minHeight } : {}}
        >
          <TableHead sx={{ backgroundColor: "rgba(189, 189, 189)" }}>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  sx={{ backgroundColor: "rgba(189, 189, 189)" }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={6} sx={{ textAlign: "center" }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            )}

            {!loading &&
              data.map((row, index) => (
                <DraggableRow
                  key={index}
                  index={index}
                  item={row}
                  moveRow={moveRow}
                />
              ))}

            {!data.length > 0 && !loading && (
              <TableRow>
                <TableCell colSpan={12} align="center">
                  <Icon fontSize={"large"}>info</Icon>
                  <Typography>Sin Registros</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </DndProvider>
  );
};

export default TableDragDrop;
